import { t } from '../i18n/index.js';
/**
 * Modal dialog component with accessibility support.
 * Focus trapping, Escape to close, ARIA roles, restore focus on close.
 */

/**
 * Show a modal dialog.
 * @param {Object} opts
 * @param {string} opts.title              — modal title
 * @param {string|HTMLElement} opts.content — body content (HTML string or DOM element)
 * @param {Array}  [opts.actions]          — footer buttons [{label, className, onClick}]
 * @param {string} [opts.size]             — 'sm' | 'md' | 'lg' | 'xl'
 * @param {Function} [opts.onOpen]         — callback when modal is opened (receives modal element)
 * @param {Function} [opts.onClose]        — callback after the modal is torn down. Use it to
 *                                           release anything onOpen acquired (PDF.js documents,
 *                                           blob URLs, observers). Fires exactly once.
 * @returns {{ close: Function, element: HTMLElement }}
 */
export function showModal({ title, content, actions = [], size = 'md', onOpen, onClose }) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.setAttribute('role', 'presentation');

  const sizeMap = { sm: '400px', md: '560px', lg: '900px', xl: 'min(95vw, 1300px)' };
  const modalId = 'modal-' + Date.now();

  overlay.innerHTML = `
    <div class="modal" role="dialog" aria-modal="true" aria-labelledby="${modalId}-title" style="max-width: ${sizeMap[size] || sizeMap.md}">
      <div class="modal-header">
        <h3 class="modal-title" id="${modalId}-title">${title}</h3>
        <button class="modal-close" aria-label="${t('modal.close_dialog')}">&times;</button>
      </div>
      <div class="modal-body" id="${modalId}-body"></div>
      ${actions.length > 0 ? '<div class="modal-footer form-actions"></div>' : ''}
    </div>
  `;

  // Populate body
  const body = overlay.querySelector('.modal-body');
  if (typeof content === 'string') {
    body.innerHTML = content;
  } else if (content instanceof HTMLElement) {
    body.appendChild(content);
  }

  // Populate footer actions
  const footer = overlay.querySelector('.modal-footer');
  if (footer) {
    for (const action of actions) {
      const btn = document.createElement('button');
      btn.className = action.className || 'btn btn-secondary';
      btn.textContent = action.label;
      btn.addEventListener('click', () => {
        if (action.onClick) action.onClick(close);
        else close();
      });
      footer.appendChild(btn);
    }
  }

  // Store previous focus to restore on close
  const previousFocus = document.activeElement;

  let closed = false;
  function close() {
    // close() is reachable from the ✕, the backdrop, Escape and any
    // action button, so it must be idempotent — onClose releases
    // resources (PDF.js documents, blob URLs) and must not run twice.
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKeydown);
    document.removeEventListener('focusin', onFocusIn, true);
    overlay.remove();
    if (previousFocus?.focus) previousFocus.focus();
    if (typeof onClose === 'function') {
      try { onClose(); } catch (err) { console.error('Modal onClose failed:', err); }
    }
  }

  // Close button + overlay click
  overlay.querySelector('.modal-close').addEventListener('click', close);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

  // BETA-00175 — comprehensive focusable selector. Covers TipTap-style
  // editors (contenteditable), media controls, anchors, and any
  // [tabindex] surfaces that components inject after the modal opens.
  // Tab handling re-queries on every keydown so dynamically-injected
  // controls (rich-text toolbars, popover content, async-loaded forms)
  // are always included.
  const FOCUSABLE_SELECTOR = [
    'a[href]:not([tabindex="-1"])',
    'area[href]:not([tabindex="-1"])',
    'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    'iframe:not([tabindex="-1"])',
    'audio[controls]:not([tabindex="-1"])',
    'video[controls]:not([tabindex="-1"])',
    '[contenteditable=""]:not([tabindex="-1"])',
    '[contenteditable="true"]:not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  function isVisible(el) {
    if (el.hasAttribute('hidden') || el.getAttribute('aria-hidden') === 'true') return false;
    // offsetParent is null for display:none elements (and for fixed
    // descendants of display:none ancestors). Cheap, sufficient here.
    if (el.offsetParent === null && getComputedStyle(el).position !== 'fixed') return false;
    const cs = getComputedStyle(el);
    if (cs.visibility === 'hidden' || cs.display === 'none') return false;
    return true;
  }

  function getFocusable() {
    const modal = overlay.querySelector('.modal');
    if (!modal) return [];
    return Array.from(modal.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isVisible);
  }

  // Keyboard handling: Escape + focus trapping
  // Only the TOP modal handles keys and traps focus. With two open (a
  // dialog opened from inside another) each trap pulled focus back from
  // the other until the browser threw "Maximum call stack size exceeded",
  // and Escape closed both at once.
  const isTop = () => [...document.querySelectorAll('.modal-overlay')].pop() === overlay;

  const onKeydown = (e) => {
    if (!isTop()) return;
    if (e.key === 'Escape') {
      close();
      return;
    }
    if (e.key === 'Tab') {
      const focusable = getFocusable();
      if (focusable.length === 0) {
        // No focusables — keep focus on the modal container itself.
        e.preventDefault();
        overlay.querySelector('.modal-close')?.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      // If focus has somehow escaped the modal entirely, pull it back
      // to the appropriate end (mirrors what focusin does, but on the
      // intentional Tab keystroke).
      if (!overlay.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };
  document.addEventListener('keydown', onKeydown);

  // Defence-in-depth: if focus escapes the modal (programmatic focus
  // call from elsewhere, or browser quirk), pull it back. Capture
  // phase so we beat any handler that would steal focus first.
  const onFocusIn = (e) => {
    if (overlay.contains(e.target) || !isTop()) return;
    const focusable = getFocusable();
    if (focusable.length > 0) focusable[0].focus();
    else overlay.querySelector('.modal-close')?.focus();
  };
  document.addEventListener('focusin', onFocusIn, true);

  // Mount and focus
  document.body.appendChild(overlay);
  requestAnimationFrame(() => {
    const firstFocusable = overlay.querySelector(
      'input, select, textarea, button:not(.modal-close)'
    );
    if (firstFocusable) firstFocusable.focus();
    else overlay.querySelector('.modal-close')?.focus();

    if (typeof onOpen === 'function') {
      onOpen(overlay.querySelector('.modal'));
    }
  });

  return { close, element: overlay };
}

/**
 * Show a confirmation dialog. Returns a Promise<boolean>.
 */
export function confirm(message, title = t('modal.confirm_title')) {
  return new Promise((resolve) => {
    showModal({
      title,
      content: `<p>${message}</p>`,
      actions: [
        { label: t('common.cancel'), className: 'btn btn-secondary', onClick: (close) => { close(); resolve(false); } },
        { label: t('common.confirm'), className: 'btn btn-primary', onClick: (close) => { close(); resolve(true); } },
      ],
    });
  });
}
